import { Checkbox, ListItemSecondaryAction, ListItemText, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import React from "react";
import { useIntl } from "react-intl";
import { ExpertRole, getExpertRoleTranslationKey } from "../../../data/constants";
import { GetTranslation } from "../../../translations/GetTranslation";
import CustomSearchBox from "../../common/customSearchBox/CustomSearchBox";
import TranslatedMessage from "../../translations/TranslatedMessage";

type RoleSelectionDialogProps = {
  isOpened: boolean;
  handleAccept: (selection: ExpertRole[]) => void;
  handleCancel: () => void;
  selectableRoles: ExpertRole[];
  selectedRoles?: ExpertRole[];
};

const RoleSelectionDialog = (props: RoleSelectionDialogProps) => {
  const { isOpened, handleAccept, handleCancel, selectableRoles } = props;
  const [selection, setSelection] = React.useState<ExpertRole[]>([]);
  const [searchToken, setSearchToken] = React.useState<string>("");
  const [filteredRoles, setFilteredRoles] = React.useState<ExpertRole[]>(selectableRoles);
  const intl = useIntl();

  React.useEffect(() => {
    setFilteredRoles(selectableRoles);
  }, [selectableRoles]);

  const onAccept = () => {
    const selectedRoles = selectableRoles.filter((role) => selection.indexOf(role) !== -1);
    handleAccept(selectedRoles);
    setSelection([]);
    setSearchToken("");
  };

  const onCancel = () => {
    handleCancel();
    setSelection([]);
    setSearchToken("");
  };

  const onSearchTokenChanged = (token: string) => {
    const nonSelectedFilteredRoles = selectableRoles.filter(
      (role) =>
        selection.indexOf(role) === -1 &&
        GetTranslation(intl, getExpertRoleTranslationKey(role)).toLowerCase().indexOf(token.toLowerCase()) !== -1
    );
    setFilteredRoles(nonSelectedFilteredRoles);
    setSearchToken(token);
  };

  const toggleRoleSelection = (role: ExpertRole) => {
    if (selection.indexOf(role) === -1) {
      setSelection([...selection, role]);
    } else {
      setSelection(selection.filter((selectedRole) => selectedRole !== role));
    }
  };

  const isListEmpty = filteredRoles.length === 0;

  return (
    <Dialog onClose={handleCancel} open={isOpened} maxWidth={"sm"} fullWidth={true}>
      <DialogTitle>
        <TranslatedMessage id="module.dialog.selectExams" />
      </DialogTitle>
      <DialogContent>
        <CustomSearchBox
          onChange={(event: any) => onSearchTokenChanged(event.target.value)}
          searchToken={searchToken}
        />

        {isListEmpty ? (
          <Typography style={emptyMessage}>
            <TranslatedMessage id="module.dialog.noAvailableExams" />
          </Typography>
        ) : (
          <List style={listContainer}>
            {filteredRoles.map((role) => (
              <ListItem button onClick={() => toggleRoleSelection(role)} key={role}>
                <ListItemText primary={GetTranslation(intl, getExpertRoleTranslationKey(role))} />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge="end"
                    onChange={() => toggleRoleSelection(role)}
                    checked={selection.indexOf(role) !== -1}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onAccept} color="primary">
          <TranslatedMessage id="common.ok" />
        </Button>
        <Button onClick={onCancel} color="primary">
          <TranslatedMessage id="common.cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const listContainer: React.CSSProperties = {
  height: 400,
  overflow: "auto",
  marginTop: 10,
  paddingTop: 0,
};

const emptyMessage: React.CSSProperties = {
  height: 400,
  marginTop: 10,
  paddingTop: 200,
  textAlign: "center",
};

export default RoleSelectionDialog;
